import { useDisclosure } from '@chakra-ui/react'
import cn from 'clsx'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'

import DialogWindow from '@/components/DialogWindow'
import SelectWithButton from '@/components/ui/select/SelectWithButton'
import Spinner from '@/components/ui/spinner/Spinner'

import Basket from '@/assets/icons/basket.svg'

import { useAuth } from '@/auth/useAuth'
import { LANGUAGES } from '@/constants/languages'

import { Button, Typography } from '../../../components/ui'
import { ITranslateParams } from '../File'
import { useFile } from '../hooks/useFile'

import CopyToolbar from './components/CopyToolbar'
import LanguageBlock from './components/LanguageBlock'
import TextDisplay from './components/TextDisplay'
import TimecodeToolbar from './components/TimecodeToolbar'
import TranslateError from './components/TranslateError'

export type TLangCodes = 'en' | 'ru' | 'ar' | 'es'

interface ITextConainerProps {
	id: number
	type: 'original' | 'translation'
	textArray: string[]
	setIsLoading: (arg: boolean) => void
	onDelete?: () => void
	onTranslate: () => Promise<unknown>
	tab: string | null
	setTab?: (arg: 'original' | 'en' | 'ru' | 'ar' | 'es') => void
	language: string[]
	isTranslateLoading?: boolean
	setIsTranslateLoading?: (arg: boolean) => void
	setRightTab: (value: string) => void
	originalLang: string
	isTranslateError: boolean
	setIsTranslateError: (value: boolean) => void
	lastTranslateParams: ITranslateParams | null
	setLastTranslateParams: (params: ITranslateParams) => void
	isChoosingLanguage: boolean
	setIsChoosingLanguage: (value: boolean) => void
	targetLang: string | null
	setTargetLang: (value: string) => void
	showChooser: boolean
	originalSourceName: string
}

const TextContainer: FC<ITextConainerProps> = ({
	id,
	type,
	textArray,
	setIsLoading,
	onDelete,
	onTranslate,
	tab,
	setTab,
	language,
	isTranslateLoading,
	setIsTranslateLoading,
	setRightTab,
	originalLang,
	isTranslateError,
	setIsTranslateError,
	setLastTranslateParams,
	lastTranslateParams,
	isChoosingLanguage,
	setIsChoosingLanguage,
	targetLang,
	setTargetLang,
	showChooser,
	originalSourceName
}) => {
	const { deleteTranslation, updateShotlist, translateShotlist } = useFile()
	const { isInt } = useAuth()

	const [isEditing, setIsEditing] = useState(false)
	const [editedText, setEditedText] = useState(textArray?.join('\n\n'))
	const [timecodePosition, setTimecodePosition] = useState<
		'left' | 'top' | 'none'
	>('left')

	const deleteText = useDisclosure()

	const excluded = [
		'original',
		...(originalLang ? [originalLang] : []),
		...(isInt ? ['ru'] : [])
	]

	const languagesCodes = Object.keys(LANGUAGES).filter(
		lang => !excluded.includes(lang)
	) as TLangCodes[]

	useEffect(() => {
		setEditedText(textArray?.join('\n\n'))
	}, [textArray])

	const displayedTextArray = useMemo(() => {
		return editedText?.split('\n\n')
	}, [editedText])

	const handleCopyText = () => {
		const clearText = textArray.join('\n').replace(/\[\d{2}:\d{2}\]\s+/g, '')

		navigator.clipboard.writeText(clearText)
	}

	const handleCopyAll = () => {
		const allText = textArray.join('\n')

		navigator.clipboard.writeText(allText)
	}

	const handleEdit = () => {
		setIsEditing(true)
	}

	const handleSave = async () => {
		try {
			setIsLoading(true)

			await updateShotlist({ translationId: id, content: editedText })

			setIsEditing(false)
		} catch (error) {
			console.error('Saving error:', error)
		} finally {
			setIsLoading(false)
		}
	}

	const handleDeleteTranslation = async () => {
		try {
			setIsLoading(true)
			await deleteTranslation(id)
			onDelete?.()
		} catch (error) {
			console.error('Deleting error:', error)
		} finally {
			setIsLoading(false)
		}
	}

	const handleConfirm = useCallback(
		async (sourceLang: string) => {
			if (!targetLang) return
			setIsChoosingLanguage(false)
			setIsTranslateLoading(true)
			setIsTranslateError(false)

			const realSourceLang =
				sourceLang === 'original' ? originalLang : sourceLang

			setLastTranslateParams({
				targetLang,
				sourceLang: realSourceLang
			})

			try {
				await translateShotlist({
					target_language: targetLang,
					source_language: realSourceLang
				})

				setRightTab(targetLang)
				await onTranslate()
			} catch (error) {
				console.error(error)
				setIsTranslateError(true)
			} finally {
				setIsTranslateLoading(false)
			}
		},
		[
			targetLang,
			originalLang,
			translateShotlist,
			onTranslate,
			setRightTab,
			setIsTranslateLoading,
			setIsChoosingLanguage,
			setIsTranslateError,
			setLastTranslateParams
		]
	)

	const onRetry = useCallback(async () => {
		if (!lastTranslateParams || isTranslateLoading) return

		const { targetLang, sourceLang } = lastTranslateParams
		setIsTranslateError(false)
		setIsTranslateLoading(true)

		try {
			await translateShotlist({
				target_language: targetLang,
				source_language: sourceLang
			})
			setRightTab(targetLang)
			await onTranslate()
		} catch (error) {
			console.error(error)
			setIsTranslateError(true)
		} finally {
			setIsTranslateLoading(false)
		}
	}, [
		lastTranslateParams,
		translateShotlist,
		onTranslate,
		setRightTab,
		isTranslateLoading,
		setIsTranslateError,
		setIsTranslateLoading
	])

	const isButtonEdit =
		(type === 'original' && tab === 'original') ||
		(type === 'translation' && tab !== 'original')

	return (
		<div className='h-full !max-h-screen flex flex-col border border-gray-200'>
			<DialogWindow
				handleClick={handleDeleteTranslation}
				open={deleteText.open}
				onClose={deleteText.onClose}
				titleText={`Delete ${
					type === 'original' ? 'original' : 'translation'
				}?`}
				noButtonText='Cancel'
				yesButtonText='Yes, delete'
				noButtonClassname='bg-gray-100 hover:bg-gray-200'
				yesButtonClassname='bg-gray-100 hover:bg-gray-200'
				yesButtonClassnameText='text-red-450'
			/>
			{/* Buttons section */}
			<section className='flex border-b py-[10px] px-8 justify-between items-center'>
				<div className='flex gap-8'>
					<TimecodeToolbar
						timecodePosition={timecodePosition}
						setTimecodePosition={setTimecodePosition}
						isTranslateLoading={isTranslateLoading}
					/>

					<CopyToolbar
						handleCopyAll={handleCopyAll}
						handleCopyText={handleCopyText}
						isTranslateLoading={isTranslateLoading}
					/>
				</div>

				<div className='flex gap-2'>
					<Button
						disabled={isTranslateLoading}
						className={cn(
							'h-fit bg-gray-100 hover:bg-gray-200 active:bg-gray-400 active:scale-y-90 !text-black !rounded-[4px] !p-2'
						)}
						onClick={() => deleteText.onOpen()}
					>
						<Basket className='w-3 h-3' />
					</Button>

					{isButtonEdit && (
						<Button
							disabled={isTranslateLoading}
							className={cn(
								'!rounded-[4px] !h-fit !px-[14px] !py-[6px] active:scale-y-90 active:bg-gray-200',
								isEditing
									? 'bg-white !text-black border hover:bg-gray-200 !px-[10px] !py-[5px]'
									: 'bg-black text-white hover:bg-gray-400'
							)}
							classNameText='!text-[12px] !leading-[16px]'
							onClick={isEditing ? handleSave : handleEdit}
						>
							{isEditing ? 'Save' : 'Edit'}
						</Button>
					)}
				</div>
			</section>

			{/* Text container */}
			<section className={cn('relative flex-1 overflow-y-auto pt-8 px-8')}>
				{isTranslateLoading && type === 'translation' ? (
					<div className='relative flex flex-col items-center justify-center top-[40%]'>
						<Spinner className='!absolute !border-t-blue-500 !border-l-gray-100 !border-r-gray-100 !border-b-gray-100' />
						<Typography variant='text' className='pt-32'>
							Translating to {LANGUAGES[targetLang]}
						</Typography>
					</div>
				) : isEditing ? (
					<textarea
						value={editedText}
						onChange={e => setEditedText(e.target.value)}
						className='w-full h-[80vh] border-2 border-blue-500 p-2 rounded'
					/>
				) : isChoosingLanguage && showChooser ? (
					<SelectWithButton
						onConfirm={handleConfirm}
						availableLangs={language}
						setIsChoosingLanguage={setIsChoosingLanguage}
					/>
				) : isTranslateError && type === 'translation' ? (
					<div className='flex items-center justify-center h-full'>
						<TranslateError
							tryCallback={onRetry}
							closeCallback={() => setIsTranslateError(false)}
						/>
					</div>
				) : (
					<TextDisplay
						displayedTextArray={displayedTextArray}
						timecodePosition={timecodePosition}
					/>
				)}
			</section>

			{/* Language section */}
			<section>
				<LanguageBlock
					setIsTranslateLoading={setIsTranslateLoading}
					onTranslate={onTranslate}
					disabled={isEditing || isTranslateLoading}
					setTab={setTab}
					language={language}
					languagesCodes={languagesCodes}
					tab={tab}
					setRightTab={setRightTab}
					originalLang={originalLang}
					setIsChoosingLanguage={setIsChoosingLanguage}
					setTargetLang={setTargetLang}
					setIsTranslateError={setIsTranslateError}
					setLastTranslateParams={setLastTranslateParams}
					originalSourceName={originalSourceName}
					type={type}
				/>
			</section>
		</div>
	)
}

export default TextContainer
